import { COMMON_METRICS } from '@/views/reports/pickers/base/columns';
import { getConversionsMetrics } from '@/views/reports/pickers/base/conversions/columns';
import { ERRORS_METRICS } from '@/views/reports/pickers/base/errors/columns';
import { LIVE_CLICKS_METRICS } from '@/views/reports/pickers/base/live-clicks/columns';
import { LIVE_VISITS_METRICS } from '@/views/reports/pickers/base/live-visits/columns';
import { POSTBACKS_METRICS } from '@/views/reports/pickers/base/postbacks/columns';
import type { DedicatedMetricsBundles } from '@/views/reports/report-metrics-runtime';
import { resolveCustomMetricsBundle } from '@/views/reports/report-metrics-runtime';
import { isDedicatedReportGroupBy } from '@/views/reports/report-route-behavior';

export type StaticMetricsBundleSlice = {
  avaliable_metrics: string[];
  report_exclude: Record<string, string[]>;
  sort_exclude?: string[];
};

export type StaticDedicatedMetricsBundles = DedicatedMetricsBundles & {
  base: StaticMetricsBundleSlice;
  conversions: StaticMetricsBundleSlice;
  postbacks: StaticMetricsBundleSlice;
  errors: StaticMetricsBundleSlice;
  'live-clicks': StaticMetricsBundleSlice;
  'live-visits': StaticMetricsBundleSlice;
};

const toBundleSlice = (options: {
  avaliable_metrics: string[];
  report_exclude?: Record<string, string[]>;
  sort_exclude?: string[];
}): StaticMetricsBundleSlice => {
  return {
    avaliable_metrics: options.avaliable_metrics,
    report_exclude: options.report_exclude || {},
    sort_exclude: options.sort_exclude,
  };
};

export const getStaticMetricsBundles = (): StaticDedicatedMetricsBundles => {
  const conversionsMetrics = getConversionsMetrics();

  return {
    base: toBundleSlice({
      avaliable_metrics: [],
      report_exclude: COMMON_METRICS.exclude,
      sort_exclude: COMMON_METRICS.sort_exclude,
    }),
    conversions: toBundleSlice({
      avaliable_metrics: conversionsMetrics.allColumns,
      sort_exclude: conversionsMetrics.sortExclude,
    }),
    postbacks: toBundleSlice({
      avaliable_metrics: POSTBACKS_METRICS.allColumns,
      sort_exclude: POSTBACKS_METRICS.sortExclude,
    }),
    errors: toBundleSlice({
      avaliable_metrics: ERRORS_METRICS.allColumns,
      sort_exclude: ERRORS_METRICS.sortExclude,
    }),
    'live-clicks': toBundleSlice({
      avaliable_metrics: LIVE_CLICKS_METRICS.allColumns,
      sort_exclude: LIVE_CLICKS_METRICS.sortExclude,
    }),
    'live-visits': toBundleSlice({
      avaliable_metrics: LIVE_VISITS_METRICS.allColumns,
      sort_exclude: LIVE_VISITS_METRICS.sortExclude,
    }),
  };
};

export const buildDedicatedTableDimensions = (
  dimensions: Record<string, unknown>,
  staticBundles: StaticDedicatedMetricsBundles = getStaticMetricsBundles(),
) => {
  return {
    ...dimensions,
    'live-visits': {
      additionalColumns: staticBundles['live-visits'].avaliable_metrics,
      mainColumn: '',
    },
    'live-clicks': {
      additionalColumns: staticBundles['live-clicks'].avaliable_metrics,
      mainColumn: '',
    },
    errors: {
      additionalColumns: staticBundles.errors.avaliable_metrics,
      mainColumn: '',
    },
    postbacks: {
      additionalColumns: staticBundles.postbacks.avaliable_metrics,
      mainColumn: '',
    },
    conversions: {
      additionalColumns: staticBundles.conversions.avaliable_metrics,
      mainColumn: '',
    },
  };
};

export const resolveReportSortExclude = (
  routeName: string,
  bundles: StaticDedicatedMetricsBundles = getStaticMetricsBundles(),
) => {
  const bundle = resolveCustomMetricsBundle(routeName, bundles) as StaticMetricsBundleSlice;
  return bundle.sort_exclude;
};

export const resolveDedicatedTableMetrics = (
  type: string,
  mainMetrics: string[],
  staticBundles: StaticDedicatedMetricsBundles = getStaticMetricsBundles(),
) => {
  if (!isDedicatedReportGroupBy(type)) {
    return mainMetrics;
  }

  const bundle = staticBundles[type as keyof StaticDedicatedMetricsBundles];
  if (!bundle || type === 'base') {
    return mainMetrics;
  }

  return bundle.avaliable_metrics;
};
